import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger
} from 'v5/design-system/DropdownMenu';
import ResourceStatusDisplayInternal from './ResourceStatusDisplayInternal';
import { Booking, ResourceSlotAssignment, Production } from '__types__';
import { Button } from 'v5/design-system/Button';
import { ResourceStatusDropdownContent } from './ResourceStatusDropdownContent';
import { rd, maybe } from '@passionware/monads';
import { DropdownMenuItem } from 'v5/design-system/DropdownMenu';
import { useDispatch } from 'react-redux';
import { openProductionSidebar } from 'store/v1/ui/ui.actions';
import { Skeleton } from 'v5/design-system/Skeleton';
import { myServices } from 'v5/services/services.connected.app';

export interface ResourceStatusDisplayProps {
  assignment?: ResourceSlotAssignment;
  booking: Booking;
  hideAssignments?: boolean;
  trimDates?: { start_date?: string; end_date?: string };
}

export function ResourceStatusDisplay({
  assignment,
  booking,
  hideAssignments,
  trimDates
}: ResourceStatusDisplayProps) {
  const dispatch = useDispatch();
  const slugAppScope = myServices.appScopeService.useSlugAppScope();

  const handleProductionClick = (production: Production) =>
    dispatch(openProductionSidebar(production, { appScope: slugAppScope }));

  return (
    <div className="tailwind-root contents">
      <ResourceStatusDisplayInternal
        assignment={assignment}
        booking={booking}
        trimDates={trimDates}
        renderAssignedProductionsInfo={
          !hideAssignments &&
          (infoText => (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  size="md"
                  className="h-auto w-auto text-left px-1 -ml-1"
                  variant="ghost"
                  onClick={e => e.stopPropagation()}
                >
                  {infoText}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-auto">
                <ResourceStatusDropdownContent booking={booking}>
                  {productionsQuery =>
                    rd
                      .journey(rd.combine(productionsQuery))
                      .wait(
                        <div className="p-4 space-y-2">
                          {Array.from({ length: 3 }, (_, index) => (
                            <div key={index} className="p-2">
                              <div className="flex items-center space-x-3">
                                <Skeleton className="w-7 h-7 rounded" />
                                <div className="flex-1 space-y-1">
                                  <Skeleton className="w-20 h-4" />
                                  <Skeleton className="w-32 h-5" />
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )
                      .catch(() => (
                        <div className="p-4 text-center">
                          <p className="text-fg-muted text-sm">
                            Error loading productions
                          </p>
                        </div>
                      ))
                      .map(productions =>
                        maybe.mapOrElse(
                          maybe.fromArray(Object.values(productions)),
                          productions => (
                            <>
                              {productions.map(production => {
                                const { id, icon, code, title } = production;
                                return (
                                  <DropdownMenuItem
                                    key={production.id}
                                    className="p-0 cursor-pointer"
                                    onSelect={() =>
                                      handleProductionClick(production)
                                    }
                                  >
                                    <div className="p-2">
                                      <div className="flex items-center space-x-3">
                                        {icon && (
                                          <div className="text-[28px]">
                                            {icon}
                                          </div>
                                        )}
                                        <div className="flex-1">
                                          <span className="text-sm text-gray-500 font-medium block">
                                            {typeof code === 'string'
                                              ? code
                                              : id}
                                          </span>
                                          {title && (
                                            <div className="text-base text-gray-800 font-semibold mt-0.5">
                                              {title}
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                  </DropdownMenuItem>
                                );
                              })}
                            </>
                          ),
                          null
                        )
                      )
                  }
                </ResourceStatusDropdownContent>
              </DropdownMenuContent>
            </DropdownMenu>
          ))
        }
      />
    </div>
  );
}

export default ResourceStatusDisplay;
